<script lang="ts">
  import { onDestroy, onMount, type Snippet } from "svelte"
  import { AiImportFlowOverlay, type AiImportFlowConfig } from "@kontextwerk/control-web-frame"
  import {
    registerEventCallback,
    unregisterEventCallback,
  } from "../../../../../../../_shared/_logic/eventbus"
  import type { CustomerImportReviewSummary } from "../../../../../../../../api/kehrblatt/imports"
  import {
    customerImportStatus,
    refreshCustomerImportStatus,
  } from "../../../../../../../_shared/_logic/customer-import-status"
  import CustomerImportCompletionStep from "./_modules/customer-import-completion-step"
  import CustomerImportReviewStep from "../../../../../../../_shared/_modules/customer-import-review-step"

  type OverlayHostProps = {
    onshowOverlay?: () => void
    setActionButtonSnippet?: (snippet: Snippet | null) => void
  }

  type Props = {
    organizationId?: string | null
    organizationSlug?: string | null
    organizationName?: string | null
    onCommitted?: () => void | Promise<void>
  }

  let {
    organizationId = null,
    organizationSlug = null,
    organizationName = null,
    onCommitted = async () => {},
    onshowOverlay = null,
    setActionButtonSnippet = null,
  }: Props & OverlayHostProps = $props()

  let latestDraftId = $state<string | null>(null)
  let reviewSummary = $state<CustomerImportReviewSummary | null>(null)
  let committedSinceOpen = false
  let committedInvalidated = false
  let committedInvalidationPromise: Promise<void> | null = null
  const sessionDraftIds = new Set<string>()

  const organizationPathSegment = $derived(organizationSlug || organizationId)

  const stringValue = (value: unknown): string | null =>
    typeof value === "string" && value.trim() ? value.trim() : null

  const objectValue = (value: unknown): Record<string, unknown> =>
    typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {}

  const eventValue = (payload: unknown): Record<string, unknown> => {
    const root = objectValue(payload)
    const widget = objectValue(root.widget)

    return Object.keys(widget).length ? widget : root
  }

  const lowerText = (value: unknown): string => stringValue(value)?.toLowerCase() ?? ""

  const draftIdFromWidgetId = (value: unknown): string | null => {
    const widgetId = stringValue(value)
    const prefix = "customer-import-"

    if (!widgetId?.startsWith(prefix)) return null

    return stringValue(widgetId.slice(prefix.length))
  }

  const looksLikeCommitProgress = (event: Record<string, unknown>): boolean => {
    const data = objectValue(event.data)
    const file = objectValue(data.file ?? event.file)
    const phase = lowerText(file.customer_import_phase ?? data.customer_import_phase)

    if (phase === "commit") return true

    const meta = objectValue(data.meta ?? event.meta)
    const labels = [
      lowerText(meta.step_label),
      lowerText(meta.stepLabel),
      lowerText(data.step_label),
      lowerText(data.stepLabel),
    ]
    const steps = Array.isArray(data.steps) ? data.steps : []
    for (const step of steps) {
      const stepRecord = objectValue(step)
      labels.push(lowerText(stepRecord.label), lowerText(stepRecord.id))
    }

    return labels.some((label) => label.includes("importlauf") || label.includes("importabschnitt"))
  }

  const extractDraftId = (payload: unknown): string | null => {
    const root = eventValue(payload)
    const data = objectValue(root.data)
    const file = objectValue(data.file ?? root.file)
    const result = objectValue(data.result ?? root.result)
    const draft = objectValue(data.draft ?? root.draft ?? result.draft)
    const explicitDraftId =
      stringValue(file.customer_import_draft_id) ??
      stringValue(data.customer_import_draft_id) ??
      stringValue(data.draft_id) ??
      stringValue(root.customer_import_draft_id) ??
      stringValue(root.draft_id) ??
      stringValue(draft.id)

    if (explicitDraftId) return explicitDraftId

    if (!looksLikeCommitProgress(root)) return null

    return draftIdFromWidgetId(data.id) ?? draftIdFromWidgetId(root.id)
  }

  const scopedImportStatus = $derived.by(() => {
    const state = $customerImportStatus

    if (organizationId && state.organizationId && state.organizationId !== organizationId)
      return null

    return state.status
  })
  const statusDraftId = $derived(stringValue(scopedImportStatus?.draft?.id))
  const reviewDraftId = $derived(latestDraftId ?? statusDraftId)
  const fallbackToLatestCommitted = $derived(!reviewDraftId && scopedImportStatus?.active === false)
  const completionSummary = $derived(
    reviewSummary && (!reviewDraftId || reviewSummary.draft_id === reviewDraftId)
      ? reviewSummary
      : null,
  )

  const runCommittedInvalidation = async () => {
    if (!committedSinceOpen || committedInvalidated) return

    if (!committedInvalidationPromise) {
      committedInvalidationPromise = Promise.resolve(onCommitted()).then(
        () => {
          committedInvalidated = true
        },
        (err) => {
          committedInvalidationPromise = null
          throw err
        },
      )
    }

    await committedInvalidationPromise
  }

  const markCommittedSummary = (summary: CustomerImportReviewSummary | null) => {
    if (!summary?.draft_id || !sessionDraftIds.has(summary.draft_id)) return

    committedSinceOpen = true
    void runCommittedInvalidation().catch((err) => {
      console.error("customer_import_committed_invalidation_failed", err)
    })
  }

  onMount(() => {
    const handlerId = `customer-ai-import-overlay-${organizationId ?? organizationSlug ?? "org"}`
    const handler = (payload: unknown) => {
      const draftId = extractDraftId(payload)

      if (draftId && draftId !== latestDraftId) {
        sessionDraftIds.add(draftId)
        latestDraftId = draftId
        reviewSummary = null
      }

      void refreshCustomerImportStatus(organizationId)
    }
    registerEventCallback("assist.import_progress", handlerId, handler)
    void refreshCustomerImportStatus(organizationId)

    return () => unregisterEventCallback("assist.import_progress", handlerId)
  })

  onDestroy(() => {
    setActionButtonSnippet?.(null)

    if (committedSinceOpen && !committedInvalidated) {
      void runCommittedInvalidation().catch((err) => {
        console.error("customer_import_close_invalidation_failed", err)
      })
    }
  })

  const flow: AiImportFlowConfig = {
    chatStartProfileId: "customer_ai_import",
    skipIntroStep: true,
    introBullets: [],
    adapters: [],
    instructionsHintText: null,
    chatStepLabel: "Import-Assistent",
    chatStepTitle: "Import-Assistent",
    chatStepHelpModalTextId: "customerImportAssistant",
    chatStepHelpModalTooltip: "Hilfe zum Kehrbezirksimport",
    postChatSteps: [
      {
        id: "data-review",
        label: "Datenprüfung",
        actionLabel: "Weiter",
        snippet: importReviewSnippet,
      },
    ],
    reviewBullets: [],
    showReviewStep: false,
  }

  const widgetContextPatch = $derived.by(() => ({
    page: "CUSTOMER_AI_IMPORT",
    taskHint:
      "Schritt 1: Datei im Import-Assistenten hochladen, Vorschau prüfen und Kehrbezirksimport starten. Schritt 2: Import-Ergebnis und Datenprüfung ansehen.",
    organizationId,
    organizationSlug,
    organizationName,
  }))
</script>

<AiImportFlowOverlay {flow} {widgetContextPatch} {onshowOverlay} {setActionButtonSnippet} />

{#snippet importReviewSnippet()}
  {#key `${reviewDraftId ?? "latest"}:${fallbackToLatestCommitted}:${organizationId ?? "current-org"}`}
    <CustomerImportReviewStep
      draftId={reviewDraftId}
      {organizationId}
      {fallbackToLatestCommitted}
      onSummaryChanged={(summary) => {
        reviewSummary = summary
        markCommittedSummary(summary)
      }}
    />
  {/key}
  {#if completionSummary}
    <CustomerImportCompletionStep {organizationPathSegment} summary={completionSummary} />
  {/if}
{/snippet}
